import { CommonModule } from '@angular/common';
import { Component, computed, input, output, signal, OnInit, AfterViewInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { CleanDeviceLabelPipe } from '../../../_pipes/clean-device-label.pipe';

type MediaDeviceOption = { media: MediaDeviceInfo; selected: boolean };

@Component({
  selector: 'app-select-camera-input-button',
  imports: [MatButtonModule, MatMenuModule, CommonModule, MatIconModule, MatTooltip, CleanDeviceLabelPipe],
  templateUrl: './select-camera-input-button.component.html',
  styleUrl: './select-camera-input-button.component.css',
  standalone: true,
})
export class SelectCameraInputButtonComponent implements OnInit, AfterViewInit {
  async ngOnInit(): Promise<void> {
    // Check if mediaDevices API is available (requires HTTPS on mobile)
    if (navigator.mediaDevices && navigator.mediaDevices.ondevicechange !== undefined) {
      navigator.mediaDevices.ondevicechange = async () => {
        this.updateDeviceList();
      };
    }
  }

  ngAfterViewInit(): void {
    // Defer device list initialization to after view init to prevent ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.updateDeviceList(false);
    }, 0);
  }

  private readonly devicesList = signal<MediaDeviceInfo[]>([]);
  private readonly selectedDeviceId = signal<string | null>(null);

  readonly cameraOptions = computed<MediaDeviceOption[]>(() => {
    const devices = this.devicesList();
    const selectedId = this.selectedDeviceId();
    return devices.map((device) => ({
      media: device,
      selected: device.deviceId === selectedId,
    }));
  });

  readonly disabled = input(false);
  readonly variant = input<'default' | 'profile'>('default');
  readonly selection = output<MediaDeviceInfo | null>();
  public selectedDevice: MediaDeviceInfo | null = null;

  public async updateDeviceList(selectDefault: boolean = false) {
    // Check if mediaDevices API is available (requires HTTPS on mobile browsers)
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.warn('MediaDevices API not available. Camera access requires HTTPS on mobile devices.');
      this.devicesList.set([]);
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((element) => element.kind === 'videoinput');
      this.devicesList.set(videoDevices);

      // Only select default device if explicitly requested and not during component initialization
      if (selectDefault && videoDevices.length > 0) {
        this.emitDefaultDevice();
      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
      this.devicesList.set([]);
    }
  }

  public emitDefaultDevice() {
    const devices = this.devicesList();
    if (devices.length > 0) {
      this.emitSelection(devices[0]);
    }
  }

  emitSelection(device: MediaDeviceInfo | null) {
    if (device) {
      this.selectedDeviceId.set(device.deviceId);
      this.selection.emit(device);
      this.selectedDevice = device;
    } else {
      this.selectedDeviceId.set(null);
      this.selection.emit(null);
      this.selectedDevice = null;
    }
  }
}

// Remove the transform function as it's no longer needed
